/** Bottom sheet — design spec §7. */

import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/cn";
import { useKeyboardInset } from "@/lib/useKeyboardInset";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** §3 — sheet titles are 18/700. */
  title?: ReactNode;
  children: ReactNode;
  /** Action row pinned under the content; stays clear of the keyboard. */
  footer?: ReactNode;
  className?: string;
}

/**
 * DS: `surface` with a hairline on top, radius 18 on the top corners,
 * `rgba(0,0,0,.45)` scrim, 36×4 grabber in `border-strong`, padding 8/16/34.
 * Grows with its content up to 85% of the screen.
 *
 * There is no close button: the spec gives the sheet a grabber, a scrim tap
 * and Telegram's own BackButton, and adding an ✕ would be a second back
 * affordance the product explicitly does without (§11).
 *
 * On a computer (≥1024 px) it is a dialog in the middle of the screen, with its
 * own scroll and no grabber: a phone's bottom sheet ran off the bottom edge and
 * hid «Выдать доступ» below the fold (PC audit 28.09, point 16).
 */
export function Sheet({ open, onClose, title, children, footer, className }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const keyboard = useKeyboardInset();

  useEffect(() => {
    if (!open) return;

    restoreFocusTo.current = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // ui-ux-pro-max: keep Tab inside the dialog while it is modal.
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // A field with autoFocus inside keeps its focus (the capture form opens ready to
    // type); otherwise the dialog itself takes it, so Tab starts inside.
    if (!panelRef.current?.contains(document.activeElement)) panelRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      restoreFocusTo.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-sheet flex flex-col justify-end lg:items-center lg:justify-center lg:p-8">
      <div
        className="absolute inset-0 animate-scrim-in bg-black/45"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cn(
          "app-container relative z-sheet flex max-h-[85%] w-full flex-col pb-[34px]",
          "animate-sheet-in rounded-t-sheet border-t border-p-border bg-p-surface outline-none focus-visible:outline-none",
          "lg:max-h-[min(80vh,760px)] lg:max-w-[560px] lg:animate-scrim-in lg:rounded-sheet lg:border lg:pb-4 lg:pt-3 lg:shadow-[0_24px_64px_rgba(0,0,0,.24)]",
          className,
        )}
        // §7 — 34px home-indicator gap normally; when the keyboard is up it is
        // replaced by the keyboard's height so the action row clears it.
        style={keyboard > 0 ? { paddingBottom: keyboard } : undefined}
      >
        <div className="flex shrink-0 justify-center pt-2 lg:hidden" aria-hidden="true">
          <span className="h-1 w-9 rounded-sm bg-p-border-strong" />
        </div>

        {title != null && (
          <h2 id={titleId} className="shrink-0 px-4 pb-1 pt-2 text-sheet font-bold text-p-text">
            {title}
          </h2>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">{children}</div>

        {footer && <div className="shrink-0 px-4 pt-2">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
